import React from 'react'
import { useParams } from 'react-router-dom'
import { VscError } from 'react-icons/vsc'

const SummonerDetailError = ({ error, countdown }) => {
    const params = useParams()

    const displayName = params.displayNameZipped || ''
    const [gameName = '', tagLine = ''] = displayName.split('-')
    const errorCode = String(error?.status_code || 'UNKNOWN')
    const errorKind = String(error?.error_kind || '').toLowerCase()
    const riotMessage = typeof error?.message === 'string' ? error.message : ''
    const isTransientIssue = errorKind === 'transient_upstream' || errorKind === 'transient_network' || ['502', '503', '504'].includes(errorCode) || errorCode === 'NETWORK'
    const isRiotIssue = errorKind.startsWith('riot_') || errorCode === '429' || errorCode === '424'
    const isServerIssue = errorKind === 'app_error' || errorCode === '500'

    const title = (() => {
        if (errorCode === '404') {
            return 'Summoner not found'
        }

        if (errorCode === '408') {
            return 'Request timed out'
        }

        if (errorCode === '429') {
            return 'Riot is rate limiting requests'
        }

        if (isTransientIssue) {
            return 'Temporary network issue'
        }

        if (isServerIssue) {
            return 'We hit a server problem'
        }

        if (isRiotIssue) {
            return 'Riot is having trouble right now'
        }

        return 'Something went wrong'
    })()

    const details = (() => {
        if (errorCode === '404') {
            return `We could not find ${gameName}#${tagLine} on this region.`
        }

        if (errorCode === '408') {
            return 'That request took too long to complete. Try again in a moment.'
        }

        if (errorCode === '429') {
            return 'Riot temporarily rate-limited this request. Try again shortly.'
        }

        if (isTransientIssue) {
            return 'A temporary gateway or network issue interrupted the request. This usually clears up on retry.'
        }

        if (isServerIssue) {
            return 'Our API had trouble finishing this request. The profile may be available again after a refresh.'
        }

        if (isRiotIssue) {
            return 'Riot services are temporarily unavailable or slow to respond for this profile.'
        }

        if (riotMessage) {
            return riotMessage
        }

        return 'Please try again later.'
    })()

    const hintLine = (() => {
        if (countdown) {
            return `Retry in ${countdown}s.`
        }

        if (errorCode === '404') {
            return 'Double-check the Riot ID and selected region.'
        }

        if (isTransientIssue) {
            return 'A refresh is often enough to recover from this type of error.'
        }

        if (isServerIssue) {
            return 'A refresh usually resolves this once the backend recovers.'
        }

        if (isRiotIssue) {
            return 'Riot may recover in a few moments.'
        }

        return null
    })()

    return (
        <div className="mx-auto mb-[80%] w-full max-w-2xl px-4 py-10 sm:py-14">
            <div className="rounded-xl border border-[#33333a] bg-black/78 p-5 shadow-[0_18px_60px_rgba(0,0,0,0.45)] sm:p-6">
                <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-rose-900/55 bg-rose-950/35">
                        <VscError className="h-7 w-7 text-rose-300" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
                            Error {errorCode}
                        </div>
                        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-100 sm:text-[1.75rem]">
                            {title}
                        </h1>
                        <p className="mt-3 max-w-prose text-sm leading-6 text-zinc-300">
                            {details}
                        </p>

                        <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-zinc-400">
                            <span className="rounded-md border border-[#33333a] bg-zinc-950 px-2 py-1">
                                {gameName && tagLine ? `${gameName}#${tagLine}` : 'Profile lookup'}
                            </span>
                            {errorKind ? (
                                <span className="rounded-md border border-[#33333a] bg-zinc-950 px-2 py-1">
                                    {errorKind.replace(/_/g, ' ')}
                                </span>
                            ) : null}
                            {isTransientIssue ? (
                                <span className="rounded-md border border-[#33333a] bg-zinc-950 px-2 py-1">
                                    Retryable
                                </span>
                            ) : null}
                            {errorCode === '500' ? (
                                <span className="rounded-md border border-[#33333a] bg-zinc-950 px-2 py-1">
                                    Internal server error
                                </span>
                            ) : null}
                        </div>

                        {hintLine ? (
                            <div className="mt-4 border-t border-[#33333a] pt-3 text-sm italic text-zinc-400">
                                {hintLine}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SummonerDetailError
